import { StyledFormControl } from "Components/Shared/FormStyles";
import { TextInput } from "Components/Shared/Inputs/TextInput";
import type { TextFieldProps } from "@mui/material";
import { formatISO } from "date-fns";
import { get } from "lodash-es";
import {
  type Control,
  Controller,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import type { JSX } from "react";

type Props<
  FormInputType extends FieldValues,
  TransformedFieldValues = FormInputType,
> = TextFieldProps & {
  name: FieldPath<FormInputType>;
  label?: string;
  control: Control<FormInputType, unknown, TransformedFieldValues>;
  errors?: FieldErrors<FormInputType>;
  formInfo?: { [key: string]: string };
  maxLength?: number;
  disabled?: boolean;
  isReadOnly?: boolean;
  mask?: string | NumberConstructor | RegExp;
  isMaskLazy?: boolean;
  maskPlaceholder?: string;
  type?: string;
  max?: number;
  min?: number;
  scale?: number;
  isNumber?: boolean;
  formControlClass?: "long" | "medium" | "tiny" | "fullWidth";
  unit?: string;
  inputStartAdornment?: JSX.Element;
  inputEndAdornment?: JSX.Element;
  valueTransformer?: (value: string) => unknown;
  isGreyIfEmpty?: boolean;
  disableHelperText?: boolean;
  disablePadding?: boolean;
  unmask?: boolean;
  onAfterChange?: () => void;
  // Runs after the field's own blur, for work that should wait until the user is done editing.
  onAfterBlur?: () => void;
};

export const FormInput = <T extends object, V extends object>(
  props: Props<T, V>,
) => {
  const {
    name,
    control,
    errors,
    formInfo,
    maxLength,
    label,
    disabled,
    isReadOnly,
    mask,
    isMaskLazy,
    maskPlaceholder,
    type,
    max,
    min,
    scale,
    isNumber,
    formControlClass,
    unit,
    isGreyIfEmpty,
    disablePadding,
    valueTransformer,
    unmask,
    onAfterChange,
    onAfterBlur,
    helperText,
    ...rest
  } = props;

  const outputTransform = (e: { target: { value: string } }) => {
    if (valueTransformer) {
      return valueTransformer(e.target.value);
    }

    // We need to convert empty string to null for required inputs for zod validation
    if (rest.required && e.target.value === "") {
      return null;
    }

    return e.target.value;
  };

  const transform =
    isNumber || type === "number"
      ? {
          input: (value: number) => {
            const isValueNumber = typeof value === "number";
            return isValueNumber ? value.toString() : "";
          },
          output: (e: { target: { value: string } }) => {
            const output = Number(e.target.value);
            return Number.isNaN(output) ? 0 : output;
          },
        }
      : type === "date"
        ? {
            input: (value?: Date | string | null) =>
              value === null || value === undefined
                ? ""
                : value instanceof Date ||
                    !Number.isNaN(new Date(value).getTime()) // is a Date, or string that creates valid Date
                  ? formatISO(new Date(value), { representation: "date" })
                  : value,
            output: (value: unknown) => value,
          }
        : {
            input: (value: any) =>
              value === null || value === undefined ? "" : value,
            output: outputTransform,
          };

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onChange, value, onBlur, ref },
        fieldState: { error },
      }) => (
        <StyledFormControl
          className={`${formControlClass} ${!label && !disablePadding ? "no-label" : ""}`}
        >
          <TextInput
            value={transform.input(value as any)}
            name={name.toString()}
            label={label}
            fieldError={errors ? get(errors, name as string) : error}
            maxLength={maxLength}
            disabled={disabled}
            isReadOnly={isReadOnly}
            onBlur={() => {
              onBlur();
              onAfterBlur?.();
            }}
            mask={mask}
            isMaskLazy={isMaskLazy}
            maskPlaceholder={maskPlaceholder}
            type={type ?? "text"}
            max={max}
            min={min}
            scale={scale}
            unit={unit}
            onInputChange={(event) => {
              const value = transform.output(event);

              onChange(value);
              onAfterChange?.();
            }}
            onMaskInputChange={(v) => {
              const value = isNumber
                ? Number(v)
                : transform.output({ target: { value: v } });

              //timeout as a walk-around to fix https://github.com/sanniassin/react-input-mask/issues/291
              setTimeout(() => {
                onChange(value);
              });
              onAfterChange?.();
            }}
            isGreyIfEmpty={isGreyIfEmpty}
            inputRef={ref}
            unmask={unmask}
            helperText={error ? error.message : helperText}
            {...rest}
          />
        </StyledFormControl>
      )}
    />
  );
};
